import type {
  CreateExpenseDto,
  PendingExpense,
  UpdateExpenseDto,
} from '@splitfiesta/shared';

/** The queued row minus its offline-only bookkeeping: the POST payload. */
export function toCreateExpenseDto(expense: PendingExpense): CreateExpenseDto {
  const {
    localId: _localId,
    ownerId: _ownerId,
    syncStatus: _syncStatus,
    retryCount: _retryCount,
    lastError: _lastError,
    lastRetryAt: _lastRetryAt,
    createdAt: _createdAt,
    predictedCategorySource: _src,
    predictedCategoryConfidence: _conf,
    pendingConversion: _pendingConversion,
    targetCurrency: _targetCurrency,
    revision: _revision,
    serverId: _serverId,
    needsServerPatch: _needsServerPatch,
    ...createData
  } = expense;
  return createData;
}

/**
 * The PATCH that makes an already-created expense match a postable create
 * payload. The conversion is always stated explicitly, because the server
 * keeps whatever conversion the earlier POST stored unless told otherwise:
 * `originalCurrency: null` drops one, and `exchangeRate: null` drops a manual
 * rate (a no-op when the stored rate is a provider one).
 */
export function toUpdateExpenseDto(dto: CreateExpenseDto): UpdateExpenseDto {
  const {
    groupId: _groupId,
    idempotencyKey: _idempotencyKey,
    // Create-only: the rule and the origin belong to the POST that stored
    // the expense.
    repeat: _repeat,
    origin: _origin,
    originalAmount,
    originalCurrency,
    exchangeRate,
    rateDate,
    // The POST already promoted the draft; resending it would ask the server
    // to promote a draft that no longer exists.
    receiptDraftId: _receiptDraftId,
    ...fields
  } = dto;
  if (!originalCurrency) return { ...fields, originalCurrency: null };
  return {
    ...fields,
    originalAmount,
    originalCurrency,
    exchangeRate: exchangeRate ?? null,
    ...(rateDate ? { rateDate } : {}),
  };
}
